import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc, getDoc, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { 
  FaSearch, 
  FaUtensils, 
  FaHamburger, 
  FaIceCream, 
  FaGlassWhiskey, 
  FaEdit, 
  FaTrash, 
  FaToggleOn, 
  FaToggleOff, 
  FaImage, 
  FaPlus, 
  FaCheck, 
  FaTimes, 
  FaSpinner, 
  FaEye, 
  FaEyeSlash,
  FaCog,
  FaExclamationTriangle,
  FaCheckCircle,
  FaList,
  FaTh,
  FaTag,
  FaEuroSign,
  FaFileAlt
} from 'react-icons/fa';
import './ViewMenuItems.css';

const sortSupplementCategories = (supplements) => {
  if (!supplements) return {};
  return Object.entries(supplements)
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce((acc, [key, value]) => {
      acc[key] = {
        ...value,
        items: value.items.sort((a, b) => a.localeCompare(b))
      };
      return acc;
    }, {});
};

const ViewMenuItems = ({ menuId }) => {
  const [categorie, setCategorie] = useState('');
  const [items, setItems] = useState([]);
  const [error] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingSupplements, setEditingSupplements] = useState(null);
  const [availableSupplements, setAvailableSupplements] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

  // Fonction pour obtenir l'icône de catégorie
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'entrees':
        return <FaUtensils className="category-icon" />;
      case 'plats':
        return <FaHamburger className="category-icon" />;
      case 'desserts':
        return <FaIceCream className="category-icon" />;
      case 'boissons':
        return <FaGlassWhiskey className="category-icon" />;
      default:
        return <FaList className="category-icon" />;
    }
  };

  // Fonction pour obtenir le nom de catégorie
  const getCategoryName = (category) => {
    switch (category) {
      case 'entrees':
        return 'Entrées';
      case 'plats':
        return 'Plats';
      case 'desserts':
        return 'Desserts';
      case 'boissons':
        return 'Boissons';
      default:
        return category;
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteDoc(doc(db, `menu/${menuId}/${categorie}`, item.id));
      setConfirmation('Élément supprimé avec succès !');
      triggerPopup();
      fetchItems(categorie);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const updatedState = !item.actif;
      const itemRef = doc(db, `menu/${menuId}/${categorie}`, item.id);
      await updateDoc(itemRef, { actif: updatedState });
      setConfirmation(`Élément ${updatedState ? 'activé' : 'désactivé'} avec succès !`);
      triggerPopup();
      fetchItems(categorie);
    } catch (error) {
      console.error('Erreur lors du changement d\'état:', error);
    }
  };

  const handleFieldUpdate = async (item, field, value) => {
    try {
      const itemRef = doc(db, `menu/${menuId}/${categorie}`, item.id);
      await updateDoc(itemRef, { [field]: value });
      setEditingField(null);
      setConfirmation('Champ mis à jour avec succès !');
      triggerPopup();
      fetchItems(categorie);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du champ:', error);
    }
  };

  const triggerPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  const handlePhotoChange = async (item, photoFile) => {
    try {
      setUploadingPhoto(item.id);
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('upload_preset', 'menu_photos');
      formData.append('cloud_name', 'dyuyrwww1');

      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/dyuyrwww1/image/upload`,
        formData
      );

      const photoURL = response.data.secure_url;
      await handleFieldUpdate(item, 'photo', photoURL);
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo:', error);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const fetchSupplements = useCallback(async () => {
    try {
      const supplementRef = doc(db, 'menu', menuId);
      const docSnapshot = await getDoc(supplementRef);
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setAvailableSupplements(sortSupplementCategories(data.suppléments || {}));
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des suppléments:', error);
      setAvailableSupplements({});
    }
  }, [menuId]);

  const fetchItems = useCallback(async (cat) => {
    try {
      setLoading(true);
      const itemsCollection = collection(db, `menu/${menuId}/${cat}`);
      const unsubscribe = onSnapshot(itemsCollection, (itemSnapshot) => {
        const itemList = itemSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setItems(itemList);
        setFilteredItems(itemList);
        setLoading(false);
      });
  
      return () => unsubscribe();
    } catch (error) {
      console.error('Erreur lors de la récupération des éléments:', error);
      setLoading(false);
    }
  }, [menuId]);  

  const handleCategoryChange = useCallback((cat) => {
    setCategorie(cat);
    fetchItems(cat);
  }, [fetchItems]);

  useEffect(() => {
    handleCategoryChange('entrees');
    fetchSupplements();
  }, [handleCategoryChange, fetchSupplements]);  

  const fetchAllItems = useCallback(async () => {
    try {
      const menuCategories = ['entrees', 'plats', 'desserts', 'boissons'];
      let allItems = [];
      
      for (const cat of menuCategories) {
        const itemsCollection = collection(db, `menu/${menuId}/${cat}`);
        const itemSnapshot = await getDocs(itemsCollection);
        const itemList = itemSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          category: cat
        }));
        allItems = [...allItems, ...itemList];
      }
      
      return allItems;
    } catch (error) {
      console.error('Erreur lors de la récupération des éléments:', error);
      return [];
    }
  }, [menuId]);

  useEffect(() => {
    if (searchTerm) {
      fetchAllItems().then(allItems => {
        const filtered = allItems.filter(item => 
          item.nom.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredItems(filtered);
      });
    } else {
      setFilteredItems(items);
    }
  }, [searchTerm, items, fetchAllItems]);

  useEffect(() => {
    handleCategoryChange('entrees');
    fetchSupplements();
  }, [handleCategoryChange, fetchSupplements]);

  const initializeEditingSupplements = (item, supplements) => {
    const initialState = {
      itemId: item.id
    };
  
    // Initialize each category with empty arrays and the new fields
    Object.keys(availableSupplements).forEach(category => {
      const categoryData = availableSupplements[category];
      initialState[category] = {
        items: (supplements && supplements[category]?.items) || [],
        selectionType: categoryData.selectionType,
        maxElement: categoryData.maxElement || 1,  // Par défaut 1 si non défini
        isRequired: categoryData.isRequired || false // Par défaut false si non défini
      };
    });
  
    return initialState;
  };

  const handleUpdateSupplements = async (item) => {
    try {
      // Vérifier que l'item existe et a un id
      if (!item || !item.id) {
        console.error('Item non trouvé ou ID manquant:', item);
        setConfirmation('Erreur: Élément non trouvé');
        triggerPopup();
        return;
      }

      // Crée une version propre des suppléments sans l'itemId
      const cleanSupplements = Object.entries(editingSupplements)
        .filter(([key]) => key !== 'itemId')
        .reduce((acc, [category, data]) => {
          if (data.items && data.items.length > 0) {
            acc[category] = {
              items: data.items,
              selectionType: data.selectionType,
              maxElement: data.maxElement,  // Ajoute maxElement
              isRequired: data.isRequired   // Ajoute isRequired
            };
          }
          return acc;
        }, {});

      const itemRef = doc(db, `menu/${menuId}/${categorie}`, item.id);
      await updateDoc(itemRef, {
        suppléments: cleanSupplements
      });
      
      setEditingSupplements(null);
      setConfirmation('Suppléments mis à jour avec succès !');
      triggerPopup();
      fetchItems(categorie);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des suppléments:', error);
      setConfirmation('Erreur lors de la mise à jour des suppléments');
      triggerPopup();
    }
  };

  return (
    <div className="view-menu-items-container">
      {/* Header */}
      <div className="view-menu-header">
        <div className="header-content">
          <h1 className="view-menu-title">
            <FaList className="title-icon" />
            Gestion du Menu
          </h1>
          <p className="view-menu-subtitle">
            Gérez vos plats, modifiez les informations et configurez les suppléments
          </p>
        </div>
      </div>

      {/* Barre de recherche et contrôles */}
      <div className="view-controls-section">
      <div className="search-container">
          <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Rechercher un plat..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

        <div className="view-controls">
          <div className="view-mode-toggle">
            <button 
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vue en grille"
            >
              <FaTh />
            </button>
            <button 
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vue en liste"
            >
              <FaList />
            </button>
          </div>
        </div>
      </div>

      {/* Navigation par catégories */}
      <div className="category-navigation">
        <button 
          className={`category-btn ${categorie === 'entrees' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('entrees')}
        >
          <FaUtensils className="btn-icon" />
          Entrées
        </button>
        <button 
          className={`category-btn ${categorie === 'plats' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('plats')}
        >
          <FaHamburger className="btn-icon" />
          Plats
        </button>
        <button 
          className={`category-btn ${categorie === 'desserts' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('desserts')}
        >
          <FaIceCream className="btn-icon" />
          Desserts
        </button>
        <button 
          className={`category-btn ${categorie === 'boissons' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('boissons')}
        >
          <FaGlassWhiskey className="btn-icon" />
          Boissons
        </button>
      </div>

      {/* Messages d'erreur et de confirmation */}
      {error && (
        <div className="error-message">
          <FaExclamationTriangle className="error-icon" />
          {error}
        </div>
      )}

      {searchTerm && filteredItems.length === 0 && (
        <div className="no-results">
          <FaSearch className="no-results-icon" />
          <p>Aucun résultat trouvé pour "{searchTerm}"</p>
        </div>
      )}

      {/* Titre de section */}
      {!searchTerm && categorie && (
        <div className="section-header">
          <h2 className="section-title">
            {getCategoryIcon(categorie)}
            {getCategoryName(categorie)}
          </h2>
          <span className="items-count">
            {items.length} {items.length === 1 ? 'élément' : 'éléments'}
          </span>
        </div>
      )}

      {/* Loading spinner */}
        {loading && (
          <div className="view-menu-loading-container">
            <div className="view-menu-loading-spinner"></div>
            <p className="view-menu-loading-text">Chargement des éléments...</p>
          </div>
        )}

      {/* Liste des éléments */}
      <div className={`items-container ${viewMode}`}>
        {(searchTerm ? filteredItems : items).map((item) => (
          <div className='menu-item-card' key={item.id}>
            {/* En-tête de la carte */}
            <div className="item-card-header">
            {searchTerm && (
                <div className="item-category-badge">
                  {getCategoryIcon(item.category)}
                  {getCategoryName(item.category)}
              </div>
              )}
              <div className={`view-menu-item-status ${item.actif ? 'active' : 'inactive'}`}>
                {item.actif ? <FaEye className="status-icon-menu" /> : <FaEyeSlash className="status-icon-menu" />}
                {item.actif ? 'Actif' : 'Inactif'}
              </div>
            </div>

            {/* Photo du plat */}
            <div className="item-photo-section">
            {item.photo ? (
              <div className="image-container">
                <img
                  src={item.photo}
                  alt={item.nom}
                  className="menu-item-photo"
                  onClick={() => document.getElementById(`fileInput-${item.id}`).click()}
                />
                  {uploadingPhoto === item.id && (
                    <div className="upload-overlay">
                      <FaSpinner className="upload-spinner" />
                    </div>
                  )}
              </div>
            ) : (
              <button
                onClick={() => document.getElementById(`fileInput-${item.id}`).click()}
                className='add-photo-button'
              >
                  <FaImage className="photo-icon" />
                  <span>Ajouter une photo</span>
              </button>
            )}
            <input
              type="file"
              id={`fileInput-${item.id}`}
              style={{ display: 'none' }}
              onChange={(e) => handlePhotoChange(item, e.target.files[0])}
            />
            </div>

            {/* Informations du plat */}
            <div className="item-info-section">
              {/* Nom du plat */}
              <div className="item-field">
                <label className="field-label">
                  <FaTag className="field-icon" />
                  Nom du plat
                </label>
                <div 
                  className={`editable-field ${editingField === `nom-${item.id}` ? 'editing' : ''}`}
                  onClick={() => setEditingField(`nom-${item.id}`)}
                >
                  {editingField === `nom-${item.id}` ? (
                    <input
                      type="text"
                      defaultValue={item.nom}
                      onBlur={(e) => handleFieldUpdate(item, 'nom', e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleFieldUpdate(item, 'nom', e.target.value)}
                      autoFocus
                      className="edit-input"
                    />
                  ) : (
                    <span className="field-value">{item.nom}</span>
                  )}
                  <FaEdit className="edit-icon" />
                </div>
              </div>

              {/* Prix */}
              <div className="item-field">
                <label className="field-label">
                  <FaEuroSign className="field-icon" />
                  Prix
                </label>
                <div 
                  className={`editable-field ${editingField === `prix-${item.id}` ? 'editing' : ''}`}
                  onClick={() => setEditingField(`prix-${item.id}`)}
                >
                  {editingField === `prix-${item.id}` ? (
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={item.prix.toFixed(2)}
                      onBlur={(e) => handleFieldUpdate(item, 'prix', parseFloat(e.target.value))}
                      onKeyPress={(e) => e.key === 'Enter' && handleFieldUpdate(item, 'prix', parseFloat(e.target.value))}
                      autoFocus
                      className="edit-input"
                    />
                  ) : (
                    <span className="field-value price">{item.prix.toFixed(2)} €</span>
                  )}
                  <FaEdit className="edit-icon" />
                </div>
              </div>

              {/* Description */}
              <div className="item-field">
                <label className="field-label">
                  <FaFileAlt className="field-icon" />
                  Description
                </label>
                <div 
                  className={`editable-field ${editingField === `description-${item.id}` ? 'editing' : ''}`}
                  onClick={() => setEditingField(`description-${item.id}`)}
                >
                  {editingField === `description-${item.id}` ? (
                    <textarea
                      defaultValue={item.description}
                      onBlur={(e) => handleFieldUpdate(item, 'description', e.target.value)}
                      autoFocus
                      className="edit-textarea"
                    />
                  ) : (
                    <span className="field-value description">{item.description}</span>
                  )}
                  <FaEdit className="edit-icon" />
                </div>
              </div>
            </div>

            {/* Section Suppléments */}
            <div className="item-supplements-section">
              <div className="supplements-header">
                <FaCog className="supplements-icon" />
                <span>Suppléments</span>
              </div>
              
              {item.suppléments && Object.keys(item.suppléments).length > 0 ? (
                <div className="supplements-display">
                  {Object.entries(sortSupplementCategories(item.suppléments)).map(([category, data]) => (
                    <div key={category} className="supplement-category-display">
                      <div className='category-title'>{category} :</div>
                      <div className='category-items'>
                        {data.items.map((supplement, index) => (
                          <span key={`${category}-${supplement}-${index}`} className="supplement-item">
                            {supplement}{index < data.items.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  <button
                    className="edit-supplements-btn"
                    onClick={() => setEditingSupplements(
                      initializeEditingSupplements(item, item.suppléments)
                    )}
                  >
                    <FaEdit className="btn-icon" />
                    Modifier
                  </button>
                </div>
              ) : (
                <button
                  className="add-supplements-btn"
                  onClick={() => setEditingSupplements(
                    initializeEditingSupplements(item)
                  )}
                >
                  <FaPlus className="btn-icon" />
                  Ajouter des suppléments
                </button>
              )}
            </div>

            {/* Actions */}
            <div className="item-actions">
              <button 
                className="action-btn view-menu-toggle-btn"
                onClick={() => handleToggleActive(item)}
              >
                {item.actif ? (
                  <>
                    <FaToggleOn className="btn-icon" />
                    Désactiver
                  </>
                ) : (
                  <>
                    <FaToggleOff className="btn-icon" />
                    Activer
                  </>
                )}
              </button>
              
              <button 
                className="action-btn delete-btn"
                onClick={() => handleDelete(item)}
              >
                <FaTrash className="btn-icon" />
                Supprimer
            </button>
            </div>
          </div>
        ))}
      </div>

      {/* Popup de modification des suppléments */}
      {editingSupplements && (
        <div className="supplements-popup-overlay" onClick={() => setEditingSupplements(null)}>
          <div className="supplements-popup" onClick={e => e.stopPropagation()}>
            <div className="supplements-popup-header">
              <h3 className="popup-title">
                <FaCog className="title-icon" />
                Modifier les Suppléments
              </h3>
              <button 
                className="supplements-popup-close-btn"
                onClick={() => setEditingSupplements(null)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="supplements-popup-body">
              {Object.entries(availableSupplements).map(([category, categoryData]) => (
                <div key={category} className="supplement-category-section">
                  <div className='category-header'>
                    <FaTag className="category-icon" />
                    <span className='category-name'>{category}</span>
                  </div>
                  <div className="supplement-items-grid">
                    {categoryData.items.map((supplement) => (
                      <label key={supplement} className="supplement-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editingSupplements[category]?.items?.includes(supplement) || false}
                          onChange={() => {
                            const currentItems = editingSupplements[category]?.items || [];
                            const newItems = currentItems.includes(supplement)
                              ? currentItems.filter(item => item !== supplement)
                              : [...currentItems, supplement];
                            
                            setEditingSupplements(prev => ({
                              ...prev,
                              [category]: {
                                items: newItems,
                                selectionType: categoryData.selectionType,
                                maxElement: prev[category]?.maxElement || categoryData.maxElement,
                                isRequired: prev[category]?.isRequired || categoryData.isRequired
                              }
                            }));
                          }}
                          className="supplement-checkbox"
                        />
                        <span className="checkbox-custom"></span>
                        <span className="supplement-name">{supplement}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="supplements-popup-footer">
              <button 
                className="validate-btn"
                onClick={() => {
                  const item = items.find(item => item.id === editingSupplements.itemId);
                  if (item) {
                    handleUpdateSupplements(item);
                  } else {
                    console.error('Item non trouvé avec ID:', editingSupplements.itemId);
                    setConfirmation('Erreur: Élément non trouvé');
                    triggerPopup();
                  }
                }}
              >
                <FaCheck className="btn-icon" />
                Valider
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setEditingSupplements(null)}
              >
                <FaTimes className="btn-icon" />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation */}
      {showPopup && (
        <div className={`confirmation-popup ${showPopup ? 'fade-in' : 'fade-out'}`}>
          <FaCheckCircle className="confirmation-icon" />
          <span className="confirmation-message">{confirmation}</span>
        </div>
      )}
    </div>
  );
};

export default ViewMenuItems;